/**
 * MaterialTabs Component Script
 * 
 * This script defines a React component named MaterialTabs that utilizes Material-UI (MUI)
 * components and styling to create a tabbed interface with sub-navigation links.
 * The component displays two main tabs: "SEAMAP Trawl" and "SEAMAP Bottom Longline",
 * each with sub-navigation links for "Stations" and "Bio".
 * 
 * Key Features:
 * - Uses MUI's `styled` utility to create customized Tab components with rounded
 *   corners, shadow effects, and color transitions for selected and unselected states.
 * - Receives two props: 
 *     • `activeTab`: the current active tab value.
 *     • `onTabChange`: a handler function to update the active tab when a user clicks on a tab.
 * - Renders the tabs inside an MUI Box with absolute positioning, making the tab interface
 *   float at the top right of the parent container.
 * - Customizes the default MUI Tabs indicator to be hidden, achieving a clean tab look.
 * 
 * Dependencies: 
 * - React
 * - @mui/material (specifically Tabs, Tab, Box, and styled)
 */

import React from 'react'
import { Tabs, Tab, Box, styled, Typography, useMediaQuery } from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { PLANKTON_WEBMAP_ENABLED } from '../features'

// style definition for the main tabs using enterprise color system
const MainTab = styled(Tab)(({ theme }) => ({
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  marginRight: 0,
  padding: '0 20px',
  minHeight: '100px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  textTransform: 'none',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  fontSize: '17px',
  fontWeight: 500,
  backgroundColor: '#20B2AA', // Light Sea Green color for unselected tabs
  color: '#FFFFFF', // White text on Light Sea Green background
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&:last-of-type': {
    paddingRight: 0
  },
  '&:not(:first-of-type)::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '20%',
    height: '60%',
    width: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)', // White with opacity for Light Sea Green background
  },
  '&:hover': {
    backgroundColor: '#1A9B99', // Slightly darker Light Sea Green on hover
    color: '#FFFFFF', // White text
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  '&.Mui-selected': {
    backgroundColor: '#F1F5F9', // Slate 100 - light background when selected
    color: '#0F172A !important', // Slate 900 - very dark text with !important to override inheritance
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    '&:not(:first-of-type)::before': {
      backgroundColor: 'rgba(15, 23, 42, 0.2)', // Slate 900 with opacity
    },
    '&:hover': {
      backgroundColor: '#E2E8F0', // Slate 200 - slightly darker on hover
      color: '#0F172A !important', // Slate 900 - maintain very dark text with !important
    },
    // Ensure all child elements also get dark text
    '& *': {
      color: '#0F172A !important',
    }
  },
  '& .MuiTab-wrapper': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  '@media (max-width: 600px)': {
    minHeight: '100px',
    minWidth: 132,
    padding: '0 10px',
    fontSize: '14px',
  }
}))

// tab click handler
export default function MaterialTabs({ activeTab, onTabChange }) {
  const isCompact = useMediaQuery('(max-width:600px)');
  const mainTab = activeTab.includes('Plankton') ? 'Plankton' : (activeTab.includes('Trawl') ? 'Trawl' : 'Longline');
  
  // Handle main tab change
  const handleTabChange = (event, newValue) => {
    // When changing main tabs, default to Stations sub-tab
    onTabChange(`${newValue}Stations`);
  };
  
  // Create the tab elements directly instead of using the TabWithSubNav component
  const createTabContent = (label, value) => {
    const isActive = activeTab.startsWith(value);
    const subPart = isActive ? (activeTab.includes('Biology') ? 'Biology' : 'Stations') : null;
    const displayLabel = isCompact
      ? (value === 'Trawl' ? 'Trawl' : (value === 'Plankton' ? 'Plankton' : 'Bottom Longline'))
      : label;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '14px', sm: '17px' },
              lineHeight: 1.25,
              fontWeight: 'inherit',
              whiteSpace: 'nowrap'
            }}
          >
            {displayLabel}
          </Typography>
          <NavigateNextIcon sx={{ 
            fontSize: '18px',
            ml: 0.5, 
            opacity: 0.7,
            transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} />
        </Box>
        {isActive && (
          <Box sx={{ 
            mt: 1, 
            fontSize: '14px',
            display: 'flex', 
            width: '100%', 
            justifyContent: 'center',
          }}>
            <Box component="span" 
              sx={{ 
                cursor: 'pointer',
                fontWeight: subPart === 'Stations' ? 600 : 500,
                padding: '2px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                backgroundColor: subPart === 'Stations' ? 'rgba(32, 178, 170, 0.2)' : 'transparent', // Soft Light Sea Green background for active
                '&:hover': {
                  backgroundColor: 'rgba(32, 178, 170, 0.1)', // Light Sea Green on hover
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTabChange(`${value}Stations`);
              }}
            >
              Stations
            </Box>
            <Box component="span" sx={{ 
              mx: 0.5, 
              opacity: 0.7,
              fontSize: '13px',
              fontWeight: 300,
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}>|</Box>
            <Box component="span" 
              sx={{ 
                cursor: 'pointer',
                fontWeight: subPart === 'Biology' ? 600 : 500,
                padding: '2px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                backgroundColor: subPart === 'Biology' ? 'rgba(32, 178, 170, 0.2)' : 'transparent', // Soft Light Sea Green background for active
                '&:hover': {
                  backgroundColor: 'rgba(32, 178, 170, 0.1)', // Light Sea Green on hover
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTabChange(`${value}Biology`);
              }}
            >
              Bio
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '100px', // Restored to original height
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end', // Align tabs to the right
      width: '100%',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden'
    }}>
      <Tabs
        value={mainTab}
        onChange={handleTabChange}
        variant={isCompact ? 'scrollable' : 'standard'}
        scrollButtons={false}
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          minHeight: '100px', // Restored to original height
          height: '100%',
          width: '100%',
          minWidth: 0,
          '& .MuiTabs-flexContainer': {
            gap: 0,
            height: '100%'
          },
          '& .MuiTabs-scroller': {
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            overflowX: { xs: 'auto', sm: 'hidden' }
          }
        }}
      >
        <MainTab 
          label={createTabContent("SEAMAP Trawl", "Trawl")}
          value="Trawl"
        />
        <MainTab 
          label={createTabContent("SEAMAP Bottom Longline", "Longline")}
          value="Longline" 
        />
        {PLANKTON_WEBMAP_ENABLED && (
          <MainTab
            label={createTabContent("SEAMAP Plankton", "Plankton")}
            value="Plankton"
          />
        )}
      </Tabs>
    </Box>
  )
}
